import * as THREE from 'three';
import { BaseScene, SceneMetadata } from './base-scene';

/**
 * Configuration for card-based scenes
 */
export interface BaseCardSceneConfig {
  cardWidth?: number;
  cardHeight?: number;
  defaultRotation?: THREE.Euler;
  defaultScale?: THREE.Vector3;
}

/**
 * Configuration for creating canvas textures
 */
export interface CanvasTextureConfig {
  width: number;
  height: number;
}

/**
 * Configuration for creating text meshes
 */
export interface TextMeshConfig {
  text: string;
  font: string;
  fontSize: number;
  color: string;
  width: number;
  height: number;
  textAlign?: CanvasTextAlign;
  textBaseline?: CanvasTextBaseline;
  backgroundColor?: string;
}

/**
 * Configuration for UI materials
 */
export interface UIMaterialConfig {
  opacity?: number;
  renderOrder?: number;
  transparent?: boolean;
  depthWrite?: boolean;
}

/**
 * Card alignment position presets
 */
export enum CardAlignment {
  CENTER = 'center',
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
  TOP_LEFT = 'top-left',
  TOP_RIGHT = 'top-right',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_RIGHT = 'bottom-right'
}

/**
 * Abstract base class for card-based AR scenes
 * Provides utilities for canvas textures, positioning, and resource management
 */
export abstract class BaseCardScene extends BaseScene {
  protected cardWidth: number;
  protected cardHeight: number;

  // Resource tracking for automatic disposal
  protected textures: THREE.Texture[] = [];
  protected materials: THREE.Material[] = [];
  protected geometries: THREE.BufferGeometry[] = [];

  // Shared animation timing
  protected elapsedTime: number = 0;

  constructor(metadata: SceneMetadata, config?: BaseCardSceneConfig) {
    super(metadata);

    this.cardWidth = config?.cardWidth ?? 1.0;
    this.cardHeight = config?.cardHeight ?? 0.65;

    if (config?.defaultRotation) {
      this.root.rotation.copy(config.defaultRotation);
    }
    if (config?.defaultScale) {
      this.root.scale.copy(config.defaultScale);
    }
  }

  /**
   * Reset elapsed time counter (useful when scene becomes visible)
   */
  protected resetElapsedTime(): void {
    this.elapsedTime = 0;
  }

  /**
   * Get the card dimensions
   */
  getCardDimensions(): { width: number; height: number } {
    return { width: this.cardWidth, height: this.cardHeight };
  }

  /**
   * Get card bounds in local coordinates
   * Card center is at (0, 0, 0)
   */
  getCardBounds(): {
    left: number;
    right: number;
    top: number;
    bottom: number;
    center: THREE.Vector3;
  } {
    const halfWidth = this.cardWidth / 2;
    const halfHeight = this.cardHeight / 2;

    return {
      left: -halfWidth,
      right: halfWidth,
      top: halfHeight,
      bottom: -halfHeight,
      center: new THREE.Vector3(0, 0, 0)
    };
  }

  /**
   * Create a canvas, context, and texture in one call
   * Automatically tracks texture for disposal
   */
  protected createCanvasTexture(config: CanvasTextureConfig): {
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    texture: THREE.CanvasTexture;
  } {
    const canvas = document.createElement('canvas');
    canvas.width = config.width;
    canvas.height = config.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context from canvas');
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.encoding = THREE.sRGBEncoding;
    texture.needsUpdate = true;

    this.textures.push(texture);

    return { canvas, ctx, texture };
  }

  /**
   * Create a text mesh with canvas texture
   * Automatically tracks texture, material, and geometry for disposal
   */
  protected createTextMesh(config: TextMeshConfig): {
    mesh: THREE.Mesh;
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    texture: THREE.CanvasTexture;
  } {
    const { canvas, ctx, texture } = this.createCanvasTexture({
      width: config.width,
      height: config.height
    });

    // Draw background if provided
    if (config.backgroundColor) {
      ctx.fillStyle = config.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // Draw text
    ctx.font = `${config.fontSize}px ${config.font}`;
    ctx.textAlign = config.textAlign ?? 'center';
    ctx.textBaseline = config.textBaseline ?? 'middle';
    ctx.fillStyle = config.color;

    const x = config.textAlign === 'left' ? 0 :
              config.textAlign === 'right' ? canvas.width :
              canvas.width / 2;
    const y = config.textBaseline === 'top' ? 0 :
              config.textBaseline === 'bottom' ? canvas.height :
              canvas.height / 2;

    ctx.fillText(config.text, x, y);
    texture.needsUpdate = true;

    // Create geometry and material
    const geometry = new THREE.PlaneGeometry(1, 1);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false
    });

    this.geometries.push(geometry);
    this.materials.push(material);

    const mesh = new THREE.Mesh(geometry, material);

    return { mesh, canvas, ctx, texture };
  }

  /**
   * Create a plane geometry sized to the card dimensions
   */
  protected createCardPlane(
    widthRatio: number = 1.0,
    heightRatio: number = 1.0,
    material?: THREE.Material
  ): THREE.Mesh {
    const geometry = new THREE.PlaneGeometry(
      this.cardWidth * widthRatio,
      this.cardHeight * heightRatio
    );

    const mat = material ?? new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true
    });

    this.geometries.push(geometry);
    if (!material) {
      this.materials.push(mat);
    }

    return new THREE.Mesh(geometry, mat);
  }

  /**
   * Create a material suitable for UI elements
   */
  protected createUIMaterial(config?: UIMaterialConfig): THREE.MeshBasicMaterial {
    const material = new THREE.MeshBasicMaterial({
      transparent: config?.transparent ?? true,
      depthWrite: config?.depthWrite ?? false,
      opacity: config?.opacity ?? 1.0
    });

    this.materials.push(material);

    return material;
  }

  /**
   * Position an object on the card using normalized coordinates
   * @param object The object to position
   * @param normalizedX X position from -0.5 (left) to 0.5 (right)
   * @param normalizedZ Z position from -0.5 (bottom) to 0.5 (top)
   * @param y Height above card surface (default: 0.01)
   */
  protected positionOnCard(
    object: THREE.Object3D,
    normalizedX: number,
    normalizedZ: number,
    y: number = 0.01
  ): void {
    object.position.set(
      normalizedX * this.cardWidth,
      y,
      normalizedZ * this.cardHeight
    );
  }

  /**
   * Position an object using a card alignment preset
   * @param object The object to position
   * @param alignment Card alignment preset
   * @param y Height above card surface (default: 0.01)
   * @param offset Optional offset from the aligned position
   */
  protected alignOnCard(
    object: THREE.Object3D,
    alignment: CardAlignment,
    y: number = 0.01,
    offset?: THREE.Vector3
  ): void {
    const bounds = this.getCardBounds();
    let x = 0;
    let z = 0;

    switch (alignment) {
      case CardAlignment.CENTER:
        x = 0;
        z = 0;
        break;
      case CardAlignment.TOP:
        x = 0;
        z = bounds.top;
        break;
      case CardAlignment.BOTTOM:
        x = 0;
        z = bounds.bottom;
        break;
      case CardAlignment.LEFT:
        x = bounds.left;
        z = 0;
        break;
      case CardAlignment.RIGHT:
        x = bounds.right;
        z = 0;
        break;
      case CardAlignment.TOP_LEFT:
        x = bounds.left;
        z = bounds.top;
        break;
      case CardAlignment.TOP_RIGHT:
        x = bounds.right;
        z = bounds.top;
        break;
      case CardAlignment.BOTTOM_LEFT:
        x = bounds.left;
        z = bounds.bottom;
        break;
      case CardAlignment.BOTTOM_RIGHT:
        x = bounds.right;
        z = bounds.bottom;
        break;
    }

    object.position.set(x, y, z);

    if (offset) {
      object.position.add(offset);
    }
  }

  /**
   * Track a texture for automatic disposal
   */
  protected trackTexture(texture: THREE.Texture): void {
    this.textures.push(texture);
  }

  /**
   * Track a material for automatic disposal
   */
  protected trackMaterial(material: THREE.Material): void {
    this.materials.push(material);
  }

  /**
   * Track a geometry for automatic disposal
   */
  protected trackGeometry(geometry: THREE.BufferGeometry): void {
    this.geometries.push(geometry);
  }

  /**
   * Dispose of all tracked resources and call parent dispose
   */
  dispose(): void {
    // Dispose tracked textures
    this.textures.forEach(texture => texture.dispose());
    this.textures = [];

    // Dispose tracked materials
    this.materials.forEach(material => material.dispose());
    this.materials = [];

    // Dispose tracked geometries
    this.geometries.forEach(geometry => geometry.dispose());
    this.geometries = [];

    // Call parent dispose
    super.dispose();
  }

  /**
   * Override setVisible to reset timing when scene becomes visible
   */
  setVisible(visible: boolean): void {
    const wasVisible = this.root.visible;
    super.setVisible(visible);

    // Reset timing if transitioning from hidden to visible
    if (!wasVisible && visible) {
      this.resetElapsedTime();
    }
  }
}
